import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Building2,
  Calendar,
  Shield,
  AlertTriangle,
  Wrench,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Phone,
  Mail,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Priority options
const PRIORITIES = [
  { id: "low", label: "Low - Can wait", color: "text-slate-600" },
  { id: "medium", label: "Medium - Needs attention soon", color: "text-amber-600" },
  { id: "high", label: "High - Urgent", color: "text-orange-600" },
  { id: "critical", label: "Critical - Emergency", color: "text-red-600" }
];

// Issue types
const ISSUE_TYPES = [
  "Equipment Malfunction",
  "Preventive Maintenance",
  "Repair Needed",
  "Calibration Required",
  "Safety Concern",
  "Performance Issue",
  "Parts Replacement",
  "Other"
];

export default function AssetLookupPage() {
  const { assetId } = useParams();
  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestForm, setRequestForm] = useState({
    issue_type: "",
    priority: "medium",
    description: "",
    contact_name: "",
    contact_email: "",
    contact_phone: ""
  });

  useEffect(() => {
    fetchAsset();
  }, [assetId]);

  const fetchAsset = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/public/assets/${assetId}`);
      if (res.ok) {
        const data = await res.json();
        setAsset(data);
      } else {
        setError("Asset not found");
      }
    } catch (err) {
      setError("Failed to load asset information");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.issue_type || !requestForm.description || !requestForm.contact_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/maintenance-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...requestForm,
          asset_id: assetId,
          asset_name: asset?.name,
          hospital_id: asset?.hospital_id,
          hospital_name: asset?.hospital_name,
          location_details: asset?.location_details
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Request submitted! Reference: ${data.request_id}`);
        setShowRequestForm(false);
        setRequestForm({
          issue_type: "",
          priority: "medium",
          description: "",
          contact_name: "",
          contact_email: "",
          contact_phone: ""
        });
      } else {
        toast.error("Failed to submit request");
      }
    } catch (err) {
      toast.error("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading asset information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Asset Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasActiveWarranty = asset?.warranties?.some(w => new Date(w.end_date) > new Date());

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{asset?.name}</h1>
                <p className="text-blue-100 text-sm">{asset?.serial_number || "No Serial Number"}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Asset Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Location</p>
                  <p className="font-medium text-slate-900">{asset?.hospital_name}</p>
                  {asset?.location_details && (
                    <p className="text-sm text-slate-500">{asset.location_details}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Installed</p>
                  <p className="font-medium text-slate-900">
                    {asset?.install_date ? new Date(asset.install_date).toLocaleDateString() : "Unknown"}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Manufacturer</p>
                  <p className="font-medium text-slate-900">{asset?.manufacturer || "—"}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Shield className={`w-5 h-5 mt-0.5 ${hasActiveWarranty ? "text-emerald-500" : "text-slate-400"}`} />
                <div>
                  <p className="text-xs text-slate-500">Warranty</p>
                  <p className={`font-medium ${hasActiveWarranty ? "text-emerald-600" : "text-slate-500"}`}>
                    {hasActiveWarranty ? "Active" : "No Active Warranty"}
                  </p>
                </div>
              </div>
            </div>

            {/* Warranty Alert */}
            {!hasActiveWarranty && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Warranty Expired</p>
                  <p className="text-sm text-amber-700">This equipment may not be covered under warranty. Service charges may apply.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="font-semibold text-slate-900 mb-4">Need Assistance?</h2>
          
          <Button 
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white py-6 text-lg"
            onClick={() => setShowRequestForm(true)}
            data-testid="create-request-btn"
          >
            <Wrench className="w-5 h-5 mr-2" />
            Report Issue / Request Service
          </Button>
          
          <p className="text-center text-sm text-slate-500 mt-4">
            Submit a maintenance request and our team will respond promptly
          </p>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-500">
          <p>Powered by <span className="font-semibold text-slate-700">AskIsaac</span></p>
          <p className="text-xs mt-1">Asset ID: {assetId}</p>
        </div>
      </div>

      {/* Request Form Modal */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Submit Service Request
            </DialogTitle>
            <DialogDescription>
              For: {asset?.name} at {asset?.hospital_name}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitRequest} className="space-y-4">
            <div>
              <Label>Issue Type *</Label>
              <Select 
                value={requestForm.issue_type} 
                onValueChange={(v) => setRequestForm({...requestForm, issue_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select issue type..." />
                </SelectTrigger>
                <SelectContent>
                  {ISSUE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priority *</Label>
              <Select 
                value={requestForm.priority} 
                onValueChange={(v) => setRequestForm({...requestForm, priority: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className={p.color}>{p.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={requestForm.description}
                onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                placeholder="Please describe the issue or service needed..."
                className="mt-1 min-h-[100px]"
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Your Contact Information</p>
              
              <div className="space-y-3">
                <div>
                  <Label>Your Name *</Label>
                  <Input
                    value={requestForm.contact_name}
                    onChange={(e) => setRequestForm({...requestForm, contact_name: e.target.value})}
                    placeholder="Full name"
                    className="mt-1"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={requestForm.contact_email}
                      onChange={(e) => setRequestForm({...requestForm, contact_email: e.target.value})}
                      placeholder="email@hospital.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={requestForm.contact_phone}
                      onChange={(e) => setRequestForm({...requestForm, contact_phone: e.target.value})}
                      placeholder="Phone number"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowRequestForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
